import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Cpu, Plus, Upload, Sparkles, Table as TableIcon, Download, ArrowLeft, ArrowRight,
  FileText, AlertTriangle, CheckCircle2, X, Trash2, MessageSquare, Copy, Lock, Unlock,
  ShieldCheck, FileSpreadsheet, FileCode2, Building2, Calendar, Wand2, HelpCircle, Mail, Circle, Info, Brain,
} from "lucide-react";

const fmt = (n) =>
  n == null || n === "" ? "—" : "£" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (n) => {
  if (n == null || n === "") return "—";
  const v = Number(n);
  return (v < 0 ? "-" : "") + "£" + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const STAGES = [
  { key: "start",     label: "Start Accounts",     icon: Building2 },
  { key: "coa",       label: "Chart of Accounts",  icon: Wand2 },
  { key: "queries",   label: "Query Management",   icon: HelpCircle },
  { key: "etb",       label: "ETB",                icon: TableIcon },
  { key: "review",    label: "Review & Approve",   icon: ShieldCheck },
];
const stageIdx = (s) => Math.max(0, STAGES.findIndex(x => x.key === s));

// Workflow status (independent of the guided stage). Palette tuned to give each state
// a distinct hue without straying from the Finchley navy/gold system.
const JOB_STATUSES = [
  { key: "open",                     label: "Open",                     cls: "border-[var(--line)] text-[var(--ink-2)] bg-white" },
  { key: "information_requested",    label: "Information Requested",    cls: "border-[#B45309] text-[#B45309] bg-[#FFFBEB]" },
  { key: "information_received",     label: "Information Received",     cls: "border-[#0369A1] text-[#0369A1] bg-[#F0F9FF]" },
  { key: "in_progress",              label: "In Progress",              cls: "border-[var(--navy)] text-[var(--navy)] bg-[var(--navy)]/5" },
  { key: "awaiting_review",          label: "Awaiting Review",          cls: "border-[#7C3AED] text-[#7C3AED] bg-[#F5F3FF]" },
  { key: "awaiting_client_approval", label: "Awaiting Client Approval", cls: "border-[#C2410C] text-[#C2410C] bg-[#FFF7ED]" },
  { key: "complete",                 label: "Completed",                cls: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" },
  { key: "on_hold",                  label: "On Hold",                  cls: "border-[#B45309] text-[#B45309] bg-[#FFF7ED]" },
];
const jobStatusMeta = (k) => JOB_STATUSES.find(x => x.key === k) || JOB_STATUSES[0];

function daysSince(iso) {
  if (!iso) return "—";
  const start = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  if (Number.isNaN(start.getTime())) return "—";
  const diff = Math.floor((Date.now() - start.getTime()) / (86_400_000));
  return diff <= 0 ? "today" : diff === 1 ? "1 day" : `${diff} days`;
}
// Numeric variant used by header filters (returns 0 when the date is missing so a "≥" filter still hides them).
function daysSinceNum(iso) {
  if (!iso) return 0;
  const start = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  if (Number.isNaN(start.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - start.getTime()) / (86_400_000)));
}

/* ==================================================================== list view */

export default function FRTX() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [openJob, setOpenJob] = useState(null);
  const [newFor, setNewFor] = useState(false);
  const [filters, setFilters] = useState({ client: "", yearEnd: "", started: "", days: "", status: "", stage: "" });

  async function load() {
    const [j, c] = await Promise.all([api.get("/frtx/jobs"), api.get("/clients")]);
    setJobs(j.data);
    setClients(c.data.filter(x => !x.archived));
  }
  useEffect(() => { load(); }, []);

  if (openJob) return <JobWorkspace jid={openJob} onBack={() => { setOpenJob(null); load(); }} clients={clients} />;

  // Counts by wizard stage (Start / COA / Queries / ETB / Review / Approved).
  const stageCounts = STAGES.map((s) => ({
    ...s,
    count: jobs.filter((j) => (j.status || "start") === s.key && j.status !== "approved").length,
  }));
  const approvedCount = jobs.filter((j) => j.status === "approved").length;
  // Counts by workflow status.
  const statusCounts = JOB_STATUSES.map((s) => ({
    ...s,
    count: jobs.filter((j) => (j.job_status || "open") === s.key).length,
  }));

  // Column-level header filters.
  const uniqueClients = Array.from(new Set(jobs.map(j => j.client_name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const uniqueYears = Array.from(new Set(
    jobs.map(j => (j.year_end_date || "").slice(0, 4)).filter(y => /^\d{4}$/.test(y))
  )).sort();
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ client: "", yearEnd: "", started: "", days: "", status: "", stage: "" });
  const hasActiveFilter = Object.values(filters).some(v => v !== "");
  const filteredJobs = jobs.filter((j) => {
    if (filters.client && (j.client_name || "") !== filters.client) return false;
    if (filters.yearEnd && !(j.year_end_date || "").includes(filters.yearEnd)) return false;
    if (filters.started && !(j.job_start_date || "").includes(filters.started)) return false;
    if (filters.days !== "" && daysSinceNum(j.job_start_date) < Number(filters.days)) return false;
    if (filters.status && (j.job_status || "open") !== filters.status) return false;
    if (filters.stage) {
      const stg = j.status === "approved" ? "approved" : (j.status || "start");
      if (stg !== filters.stage) return false;
    }
    return true;
  });

  async function setJobStatus(jid, status) {
    await api.patch(`/frtx/jobs/${jid}`, { job_status: status });
    load();
  }

  return (
    <div className="p-6 max-w-7xl">
      <div className="overline text-xs mb-1">Practice OS · FRTX</div>
      <h1 className="font-display text-4xl mb-1">FRTX <span className="text-[var(--ink-2)] text-lg font-sans font-normal">· Automated Accounts</span></h1>
      <p className="text-sm text-[var(--ink-2)] mb-5">Guided end-to-end flow — from bank statement to signed-off ETB. Chart of Accounts is derived from your firm&apos;s ETB template.</p>

      {/* Summary tiles — Stages */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-3" data-testid="frtx-stage-counts">
        {stageCounts.map((s) => (
          <div key={s.key} className="card-flat px-3 py-2 flex items-center gap-2">
            <s.icon size={13} className="text-[var(--navy)] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] truncate">{s.label}</div>
              <div className="text-lg font-display leading-tight">{s.count}</div>
            </div>
          </div>
        ))}
        <div className="card-flat px-3 py-2 flex items-center gap-2 border-[var(--ok)]/40">
          <Lock size={13} className="text-[var(--ok)] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-[var(--ok)] truncate">Approved</div>
            <div className="text-lg font-display leading-tight text-[var(--ok)]">{approvedCount}</div>
          </div>
        </div>
      </div>

      {/* Summary tiles — Workflow status */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="frtx-status-counts">
        {statusCounts.map((s) => (
          <span key={s.key} className={`status-pill text-[10px] ${s.cls}`}>
            <span className="font-medium mr-1">{s.count}</span> {s.label}
          </span>
        ))}
      </div>

      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-[var(--ink-2)] flex items-center gap-3">
          <span>
            {hasActiveFilter
              ? <>Showing <b>{filteredJobs.length}</b> of {jobs.length} account{jobs.length === 1 ? "" : "s"}</>
              : <>{jobs.length} account{jobs.length === 1 ? "" : "s"}</>}
          </span>
          {hasActiveFilter && (
            <button
              data-testid="frtx-clear-filters"
              onClick={clearFilters}
              className="text-[10px] uppercase tracking-wider text-[var(--navy)] hover:opacity-70"
            >Clear filters</button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/frtx/memory"
            data-testid="frtx-memory-link"
            title="View the firm-wide coding rules the AI has learned from your corrections"
            className="btn-ghost flex items-center gap-1.5 text-xs !py-2 !px-3"
          >
            <Brain size={13} strokeWidth={1.5} /> Firm Memory
          </Link>
          <button data-testid="frtx-new-job" onClick={() => setNewFor(true)} className="btn-primary flex items-center gap-2 text-sm !py-2 !px-3">
            <Plus size={14} strokeWidth={1.5} /> New Accounts
          </button>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>Client</th>
              <th>Year End</th>
              <th>Started</th>
              <th className="text-right">Days</th>
              <th>Status</th>
              <th>Stage</th>
              <th className="text-right">Txns</th>
              <th className="text-right">Queries</th>
              <th className="text-right">Actions</th>
            </tr>
            <tr className="bg-[var(--bg-2)]">
              <th className="!py-1 !px-1.5">
                <select
                  data-testid="frtx-filter-client"
                  value={filters.client}
                  onChange={(e) => setFilter("client", e.target.value)}
                  className="w-full text-[10px] px-1.5 py-0.5 border border-[var(--line)] rounded bg-white font-sans font-normal"
                  title="Filter by client"
                >
                  <option value="">All clients</option>
                  {uniqueClients.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </th>
              <th className="!py-1 !px-1.5">
                <select
                  data-testid="frtx-filter-year-end"
                  value={filters.yearEnd}
                  onChange={(e) => setFilter("yearEnd", e.target.value)}
                  className="w-full text-[10px] px-1.5 py-0.5 border border-[var(--line)] rounded bg-white font-sans font-normal"
                  title="Filter by year-end year"
                >
                  <option value="">All years</option>
                  {uniqueYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </th>
              <th className="!py-1 !px-1.5">
                <input
                  data-testid="frtx-filter-started"
                  value={filters.started}
                  onChange={(e) => setFilter("started", e.target.value)}
                  placeholder="e.g. 2025-12"
                  className="w-full text-[10px] px-1.5 py-0.5 border border-[var(--line)] rounded bg-white font-mono-data font-normal"
                />
              </th>
              <th className="!py-1 !px-1.5">
                <input
                  data-testid="frtx-filter-days"
                  type="number"
                  min="0"
                  value={filters.days}
                  onChange={(e) => setFilter("days", e.target.value)}
                  placeholder="≥ days"
                  className="w-full text-[10px] px-1.5 py-0.5 border border-[var(--line)] rounded bg-white font-mono-data font-normal text-right"
                />
              </th>
              <th className="!py-1 !px-1.5">
                <select
                  data-testid="frtx-filter-status"
                  value={filters.status}
                  onChange={(e) => setFilter("status", e.target.value)}
                  className="w-full text-[10px] px-1.5 py-0.5 border border-[var(--line)] rounded bg-white font-sans font-normal"
                >
                  <option value="">All statuses</option>
                  {JOB_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </th>
              <th className="!py-1 !px-1.5">
                <select
                  data-testid="frtx-filter-stage"
                  value={filters.stage}
                  onChange={(e) => setFilter("stage", e.target.value)}
                  className="w-full text-[10px] px-1.5 py-0.5 border border-[var(--line)] rounded bg-white font-sans font-normal"
                >
                  <option value="">All stages</option>
                  {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  <option value="approved">Approved</option>
                </select>
              </th>
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map((j) => {
              const meta = jobStatusMeta(j.job_status || "open");
              return (
                <tr key={j.id} data-testid={`frtx-job-${j.id}`}>
                  <td className="font-medium">{j.client_name || "—"}</td>
                  <td className="font-mono-data text-xs">{j.year_end_date || "—"}</td>
                  <td className="font-mono-data text-xs">{j.job_start_date || "—"}</td>
                  <td className="text-right font-mono-data text-xs text-[var(--ink-2)]" data-testid={`frtx-days-${j.id}`}>{daysSince(j.job_start_date)}</td>
                  <td>
                    <select
                      data-testid={`frtx-status-${j.id}`}
                      value={j.job_status || "open"}
                      onChange={(e) => setJobStatus(j.id, e.target.value)}
                      className={`status-pill text-[10px] cursor-pointer ${meta.cls}`}
                      title="Change workflow status"
                    >
                      {JOB_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <span className={`status-pill text-[10px] ${j.status === "approved" ? "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" : "border-[var(--line)] text-[var(--ink-2)]"}`}>
                      {j.status === "approved" ? <><Lock size={9} className="inline mr-1" /> approved</> : STAGES.find(s => s.key === j.status)?.label || j.status || "draft"}
                    </span>
                  </td>
                  <td className="text-right font-mono-data text-xs">{j.tx_count || 0}</td>
                  <td className={`text-right font-mono-data text-xs ${j.query_count > 0 ? "text-[var(--warn)]" : "text-[var(--ok)]"}`}>{j.query_count || 0}</td>
                  <td className="text-right">
                    <div className="flex gap-2 justify-end">
                      <button data-testid={`frtx-open-${j.id}`} onClick={() => setOpenJob(j.id)} className="btn-ghost !py-1 !px-2 text-xs">Open</button>
                      <button data-testid={`frtx-del-${j.id}`} onClick={async () => {
                        if (!window.confirm(`Delete accounts for ${j.client_name}? All data will be lost.`)) return;
                        await api.delete(`/frtx/jobs/${j.id}`); load();
                      }} className="text-[var(--err)] hover:opacity-70 text-xs p-1"><Trash2 size={13} strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredJobs.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-[var(--ink-2)]">
                <Cpu size={20} strokeWidth={1} className="inline mb-2 opacity-50" /><br />
                {jobs.length === 0
                  ? <>No accounts yet. Click <b>New Accounts</b> to start.</>
                  : <>No accounts match the current filters. <button onClick={clearFilters} className="text-[var(--navy)] underline">Clear filters</button></>}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {newFor && <NewJobModal clients={clients} onClose={(saved) => { setNewFor(false); if (saved) { load(); setOpenJob(saved); } }} />}
    </div>
  );
}

function NewJobModal({ clients, onClose }) {
  const [clientId, setClientId] = useState("");
  const [ye, setYe] = useState("");
  const [err, setErr] = useState("");
  const selected = clients.find(c => c.id === clientId);
  async function save() {
    if (!clientId) return setErr("Pick a client");
    if (!ye) return setErr("Choose year-end date");
    try {
      const r = await api.post("/frtx/jobs", { client_id: clientId, year_end_date: ye });
      onClose(r.data.id);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--surface)] rounded shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-display text-2xl">New Accounts</h2>
          <button onClick={() => onClose(null)} className="btn-ghost !p-1"><X size={16} /></button>
        </div>
        {err && <div className="text-xs text-[var(--err)] mb-3" data-testid="new-job-err">{err}</div>}
        <label className="label-overline">Client</label>
        <select data-testid="new-job-client" className="input mb-3" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Select client…</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
        </select>
        {selected && (
          <div className="text-xs text-[var(--ink-2)] mb-3 space-y-0.5 bg-[var(--bg)] p-2 rounded">
            <div>Company: {selected.business_name || selected.client_name}</div>
            <div>No: {selected.registered_no || "—"}</div>
          </div>
        )}
        <label className="label-overline">Year End Date</label>
        <input data-testid="new-job-ye" type="date" className="input mb-4" value={ye} onChange={(e) => setYe(e.target.value)} />
        <button data-testid="new-job-save" onClick={save} className="btn-primary w-full">Create Accounts</button>
      </div>
    </div>
  );
}

/* ==================================================================== workspace */

function JobWorkspace({ jid, onBack, clients }) {
  const [job, setJob] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [tab, setTab] = useState("start");

  async function load() {
    const [j, r] = await Promise.all([
      api.get(`/frtx/jobs/${jid}`),
      api.get(`/frtx/jobs/${jid}/readiness`).catch(() => ({ data: null })),
    ]);
    setJob(j.data); setReadiness(r.data);
    // On first load, jump to the current stage of the wizard.
    setTab((t) => t === "start" && j.data.status && j.data.status !== "start" ? j.data.status : t);
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [jid]);

  if (!job) return <div className="p-6">Loading…</div>;

  const isApproved = job.status === "approved";
  const currentIdx = stageIdx(isApproved ? "review" : job.status);

  return (
    <div className="p-6 max-w-full">
      <button data-testid="frtx-back" onClick={onBack} className="text-xs text-[var(--ink-2)] hover:text-[var(--ink)] flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Back to engagements
      </button>
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="overline text-xs mb-1">FRTX · Engagement</div>
          <h1 className="font-display text-3xl mb-1">{job.client_name}</h1>
          <div className="text-sm text-[var(--ink-2)]">
            Year end {job.year_end_date} · {job.transactions?.length || 0} transactions
          </div>
        </div>
      </div>

      {isApproved && (
        <div className="card-flat p-3 mb-5 border-l-4 border-l-[var(--ok)] bg-[var(--ok-bg)] flex items-center gap-3" data-testid="frtx-approved-banner">
          <Lock size={16} className="text-[var(--ok)]" />
          <div className="flex-1">
            <div className="text-sm font-medium">Approved &amp; locked</div>
            <div className="text-xs text-[var(--ink-2)]">
              by {job.approved_by_name || "—"} · {(job.approved_at || "").slice(0, 16).replace("T", " ")}
            </div>
          </div>
          <button data-testid="frtx-jump-review" onClick={() => setTab("review")} className="btn-ghost text-xs flex items-center gap-2"><Unlock size={12} /> Review / Exports</button>
        </div>
      )}

      {/* Wizard step indicator */}
      <div className="flex items-center gap-1 mb-6" data-testid="frtx-stepper">
        {STAGES.map((s, i) => {
          const done = i < currentIdx || isApproved;
          const active = tab === s.key;
          const I = s.icon;
          return (
            <React.Fragment key={s.key}>
              <button
                data-testid={`frtx-tab-${s.key}`}
                onClick={() => setTab(s.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded text-xs transition ${
                  active ? "bg-[var(--navy)] text-white" :
                  done ? "bg-[var(--ok-bg)] text-[var(--ok)] hover:bg-[var(--ok-bg)]/70" :
                  "text-[var(--ink-2)] hover:bg-[var(--bg)]"
                }`}
              >
                {done && !active ? <CheckCircle2 size={13} /> : <I size={13} />}
                <span>{i + 1}. {s.label}</span>
              </button>
              {i < STAGES.length - 1 && <div className="w-4 h-px bg-[var(--line)]" />}
            </React.Fragment>
          );
        })}
      </div>

      {tab === "start"   && <StartTab job={job} clients={clients} onReload={load} onNext={() => setTab("coa")} />}
      {tab === "coa"     && <COATab   job={job} onReload={load} onNext={() => setTab("queries")} />}
      {tab === "queries" && <QueriesTab job={job} onReload={load} onNext={() => setTab("etb")} />}
      {tab === "etb"     && <ETBTab   job={job} onReload={load} onNext={() => setTab("review")} />}
      {tab === "review"  && <ReviewTab job={job} readiness={readiness} onReload={load} />}
    </div>
  );
}

/* ==================================================================== Stage 1: Start Accounts */

function StartTab({ job, clients, onReload, onNext }) {
  const [form, setForm] = useState({
    company_name: job.company_name || "",
    company_number: job.company_number || "",
    registered_address: job.registered_address || "",
    correspondence_address: job.correspondence_address || "",
    year_end_date: job.year_end_date || "",
    notes: job.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const locked = job.status === "approved";

  async function save() {
    setErr(""); setBusy(true);
    try { await api.patch(`/frtx/jobs/${job.id}`, form); await onReload(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy(false);
  }
  async function start() {
    await save();
    if (err) return;
    try {
      await api.post(`/frtx/jobs/${job.id}/advance?to=coa`);
      await onReload();
      onNext();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-3xl">
      <div className="card-flat p-5 mb-4">
        <h2 className="font-display text-xl mb-1 flex items-center gap-2"><Building2 size={18} /> Start Accounts</h2>
        <p className="text-xs text-[var(--ink-2)] mb-5">Confirm the client details for this engagement. Anything blank in the client record can be filled in here.</p>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-overline">Client</label>
            <div className="input bg-[var(--bg)] text-sm">{job.client_name}</div>
          </div>
          <div>
            <label className="label-overline">Year End Date</label>
            <input data-testid="start-ye" type="date" className="input" value={form.year_end_date} onChange={set("year_end_date")} disabled={locked} />
          </div>
          <div className="col-span-2">
            <label className="label-overline">Company Name</label>
            <input data-testid="start-cname" className="input" value={form.company_name} onChange={set("company_name")} disabled={locked} />
          </div>
          <div>
            <label className="label-overline">Company Number</label>
            <input data-testid="start-cno" className="input" value={form.company_number} onChange={set("company_number")} disabled={locked} />
          </div>
          <div />
          <div className="col-span-2">
            <label className="label-overline">Registered Address</label>
            <textarea data-testid="start-ra" className="input h-20 text-sm" value={form.registered_address} onChange={set("registered_address")} disabled={locked} />
          </div>
          <div className="col-span-2">
            <label className="label-overline">Correspondence Address</label>
            <textarea data-testid="start-ca" className="input h-20 text-sm" value={form.correspondence_address} onChange={set("correspondence_address")} disabled={locked} />
          </div>
          <div className="col-span-2">
            <label className="label-overline">Engagement Notes</label>
            <textarea data-testid="start-notes" className="input h-16 text-sm" value={form.notes} onChange={set("notes")} disabled={locked} />
          </div>
        </div>

        {err && <div className="text-xs text-[var(--err)] mt-3" data-testid="start-err">{err}</div>}

        <div className="flex justify-end gap-2 mt-5">
          <button data-testid="start-save" onClick={save} disabled={busy || locked} className="btn-ghost text-sm">Save</button>
          <button data-testid="start-begin" onClick={start} disabled={busy || locked || !form.year_end_date} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
            Start Accounts <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== Stage 2: Chart of Accounts */

function COATab({ job, onReload, onNext }) {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [coa, setCoa] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [showCoaMgr, setShowCoaMgr] = useState(false);
  const [batches, setBatches] = useState([]);
  const [uploadDlg, setUploadDlg] = useState(null); // { file, dateFrom, dateTo }
  const bankRef = useRef(null);
  const locked = job.status === "approved";
  const txs = job.transactions || [];

  async function reloadCoa() { const r = await api.get("/frtx/coa"); setCoa(r.data); }
  async function reloadBatches() {
    try { const r = await api.get(`/frtx/jobs/${job.id}/batches`); setBatches(r.data); } catch { /* endpoint just added — silent if 404 */ }
  }
  useEffect(() => { reloadCoa(); reloadBatches(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { reloadBatches(); /* eslint-disable-next-line */ }, [job.transactions?.length]);

  async function archiveBatch(b) {
    if (!window.confirm(`Archive upload "${b.filename}"? Its ${b.tx_count || 0} transaction(s) will be hidden from the ETB / totals until unarchived.`)) return;
    setErr(""); setBusy(`Archiving ${b.filename}…`);
    try {
      const r = await api.post(`/frtx/jobs/${job.id}/batches/${b.id}/archive`);
      setErr(`✓ Archived — ${r.data.transactions_archived} transaction(s) hidden.`);
      await onReload();
      await reloadBatches();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy("");
  }
  async function unarchiveBatch(b) {
    setErr(""); setBusy(`Restoring ${b.filename}…`);
    try {
      const r = await api.post(`/frtx/jobs/${job.id}/batches/${b.id}/unarchive`);
      setErr(`✓ Restored — ${r.data.transactions_unarchived} transaction(s) back in view.`);
      await onReload();
      await reloadBatches();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy("");
  }
  async function deleteBatch(b) {
    if (!isAdmin) { setErr("Only admins can permanently delete an upload. Try Archive instead."); return; }
    if (!window.confirm(`Permanently delete "${b.filename}" and its ${b.tx_count || 0} transaction(s)?\n\nThis cannot be undone. Consider Archiving first if you're not sure.`)) return;
    setErr(""); setBusy(`Deleting ${b.filename}…`);
    try {
      const r = await api.delete(`/frtx/jobs/${job.id}/batches/${b.id}`);
      setErr(`✓ Removed ${r.data.transactions_deleted} transaction(s) from "${b.filename}".`);
      await onReload();
      await reloadBatches();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy("");
  }

  async function exportCoa() {
    setErr(""); setBusy("Preparing COA.xlsx…");
    try {
      const r = await fetch(`${API}/frtx/jobs/${job.id}/export/coa`, { credentials: "include" });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || `HTTP ${r.status}`); }
      const blob = await r.blob();
      const slug = (job.client_name || "client").replace(/[^A-Za-z0-9]+/g, "_");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `FRTX_COA_${slug}_${job.year_end_date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setErr(e.message); }
    setBusy("");
  }

  async function uploadFile(file, dateFrom, dateTo) {
    setErr(""); setBusy("Uploading bank CSV…");
    const fd = new FormData();
    fd.append("file", file);
    if (dateFrom) fd.append("date_from", dateFrom);
    if (dateTo) fd.append("date_to", dateTo);
    try {
      const r = await fetch(`${API}/frtx/jobs/${job.id}/upload-bank`, { method: "POST", body: fd, credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail || j));
      if (j.transactions_loaded === 0) {
        setErr("Uploaded but 0 transactions parsed. Check the Date/Description/Amount columns in your CSV.");
      } else {
        const extras = j.out_of_range ? ` (${j.out_of_range} outside date range)` : "";
        setErr(`✓ Imported ${j.transactions_loaded} transaction(s)${extras}.`);
      }
      setUploadDlg(null);
      await onReload();
      await reloadBatches();
    } catch (e) { setErr(e.message); }
    setBusy("");
  }
  async function codeAll() {
    setErr(""); setBusy("Starting AI coding…");
    try {
      await api.post(`/frtx/jobs/${job.id}/code`);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
      setBusy("");
      return;
    }
    // Poll coding-status every 2s until done or error. Reload the job state on each tick so the
    // transaction table paints new codes as they land.
    let elapsed = 0;
    while (elapsed < 600) { // hard-cap 10min
      await new Promise(r => setTimeout(r, 2000));
      elapsed += 2;
      let s;
      try { s = (await api.get(`/frtx/jobs/${job.id}/coding-status`)).data; }
      catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); break; }
      if (s.status === "running") {
        setBusy(`Coding ${s.done}/${s.total} · ${s.queries} queries so far…`);
        await onReload();
      } else if (s.status === "error") {
        setErr(s.error || "AI coding failed. Please retry.");
        await onReload();
        break;
      } else {
        setBusy("");
        await onReload();
        return;
      }
    }
    setBusy("");
  }
  async function complete() {
    setErr("");
    try { await api.post(`/frtx/jobs/${job.id}/advance?to=queries`); await onReload(); onNext(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }

  // "Uncoded" = truly uncoded OR "orphaned" (coded to a category that no longer exists
  // in the current chart — happens when the template has changed since coding). Both look
  // the same in the UI (dropdown shows "— select —") so we flag both here.
  const coaNames = useMemo(() => new Set(coa.map(c => c.name)), [coa]);
  const uncoded = txs.filter(t => !t.coa_name || !coaNames.has(t.coa_name)).length;
  const orphaned = txs.filter(t => t.coa_name && !coaNames.has(t.coa_name)).length;

  return (
    <div>
      <div className="card-flat px-4 py-2.5 mb-2">
        <div className="flex items-center gap-3 flex-wrap">
          <Wand2 size={14} className="text-[var(--navy)] shrink-0" />
          <div className="flex flex-col leading-tight mr-2">
            <span className="font-display text-sm">Chart of Accounts</span>
            <span className="text-[10px] text-[var(--ink-2)]">AI codes each transaction to your ETB categories.</span>
          </div>

          <button data-testid="coa-bank-upload" disabled={locked} onClick={() => setUploadDlg({ file: null, dateFrom: "", dateTo: "" })} className="btn-ghost !py-1 !px-2 text-[11px] flex items-center gap-1.5 disabled:opacity-40">
            <Upload size={11} /> Bank CSV
          </button>
          <input ref={bankRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && setUploadDlg((s) => ({ ...(s || { dateFrom: "", dateTo: "" }), file: e.target.files[0] }))} />
          {job.bank_filename && (
            <span className="text-[10px] text-[var(--ink-2)] truncate max-w-[240px]" title={`${job.bank_filename} · ${txs.length} rows`}>
              ✓ {job.bank_filename} · {txs.length} rows
            </span>
          )}

          <div className="flex items-center gap-1.5 ml-auto">
            <button data-testid="coa-manage" disabled={locked} onClick={() => setShowCoaMgr(v => !v)} className="btn-ghost !py-1 !px-2 text-[11px] flex items-center gap-1.5 disabled:opacity-40" title="View / add / remove Chart of Accounts categories">
              <FileText size={11} /> {showCoaMgr ? "Hide" : "Manage"} COA
            </button>
            <button data-testid="coa-export" disabled={busy || locked} onClick={exportCoa} className="btn-ghost !py-1 !px-2 text-[11px] flex items-center gap-1.5 disabled:opacity-40" title="Download current COA + running totals (partial or final)">
              <Download size={11} /> Export COA
            </button>
            <button data-testid="coa-code" disabled={!txs.length || busy || locked} onClick={codeAll} className="btn-primary !py-1 !px-2.5 text-[11px] flex items-center gap-1.5 disabled:opacity-40">
              <Sparkles size={11} /> AI Code
            </button>
            <button
              data-testid="coa-complete"
              disabled={locked || !txs.length || uncoded > 0}
              onClick={complete}
              className={`!py-1 !px-2.5 text-[11px] flex items-center gap-1.5 disabled:opacity-60 rounded ${uncoded > 0 ? "btn-primary !bg-[var(--err)] hover:!bg-[var(--err)]/90 !text-white" : "btn-ghost"}`}
              title={uncoded ? `${uncoded} transaction(s) uncoded — code them (or raise queries) before completing` : ""}
            >
              {uncoded > 0 ? <><AlertTriangle size={11} /> {uncoded} uncoded</> : <>COA Complete <ArrowRight size={11} /></>}
            </button>
          </div>
        </div>

        {(busy || err || job.bank_column_map_source || txs.length > 0) && (
          <div className="mt-1.5 pt-1.5 border-t border-[var(--line)]/60 text-[10px] flex items-center gap-3 flex-wrap">
            {busy && <span className="text-[var(--ink-2)] animate-pulse">{busy}</span>}
            {err && <span className="text-[var(--err)]" data-testid="coa-err">⚠ {err}</span>}
            {!busy && !err && job.bank_column_map_source === "learned" && (
              <span className="text-[var(--ok)]" data-testid="coa-map-source">✓ Bank column mapping learned from previous upload.</span>
            )}
            {!busy && !err && job.bank_column_map_source === "ai-detected" && (
              <span className="text-[var(--navy)]" data-testid="coa-map-source">✨ AI detected the CSV column layout — instant next time for this client.</span>
            )}
            <span className="text-[var(--ink-2)] ml-auto" data-testid="coa-uncoded-count">
              {uncoded} uncoded{orphaned > 0 ? ` (incl. ${orphaned} orphaned)` : ""} · {txs.filter(t => t.is_query).length} query
            </span>
          </div>
        )}
      </div>

      {showCoaMgr && <CoaManager coa={coa} locked={locked} onChange={reloadCoa} />}

      {batches.length > 0 && (
        <div className="card-flat px-3 py-2 mb-2" data-testid="batch-strip">
          <div className="flex items-center gap-2 flex-wrap text-[10px]">
            <span className="text-[var(--ink-2)] uppercase tracking-wider mr-1">Uploads:</span>
            {batches.map((b) => (
              <span key={b.id} className={`inline-flex items-center gap-1.5 border rounded px-2 py-0.5 ${b.archived ? "border-dashed border-[var(--ink-2)]/40 bg-[var(--ink-2)]/5 opacity-70" : "border-[var(--line)]"}`} data-testid={`batch-${b.id}`}>
                <FileSpreadsheet size={10} className="text-[var(--ink-2)]" />
                <span className="text-[var(--ink-1)] font-medium max-w-[220px] truncate" title={b.filename}>{b.filename}</span>
                {b.legacy && <span className="text-[8px] px-1 py-0.5 rounded bg-[var(--ink-2)]/10 text-[var(--ink-2)] uppercase">legacy</span>}
                {b.archived && <span className="text-[8px] px-1 py-0.5 rounded bg-[var(--ink-2)]/20 text-[var(--ink-2)] uppercase">archived</span>}
                <span className="text-[var(--ink-2)] font-mono-data">
                  {b.tx_count || 0} txs · {b.date_from || "?"} → {b.date_to || "?"}
                </span>
                {b.archived ? (
                  <button
                    data-testid={`batch-unarch-${b.id}`}
                    disabled={locked || !!busy}
                    onClick={() => unarchiveBatch(b)}
                    className="text-[var(--ok)] hover:opacity-70 disabled:opacity-30"
                    title="Restore this upload"
                  >
                    <Unlock size={10} />
                  </button>
                ) : (
                  <button
                    data-testid={`batch-arch-${b.id}`}
                    disabled={locked || !!busy}
                    onClick={() => archiveBatch(b)}
                    className="text-[var(--ink-2)] hover:text-[var(--navy)] disabled:opacity-30"
                    title={`Archive this upload (hides its ${b.tx_count || 0} transaction(s) from ETB/totals)`}
                  >
                    <Lock size={10} />
                  </button>
                )}
                {isAdmin && (
                  <button
                    data-testid={`batch-del-${b.id}`}
                    disabled={locked || !!busy}
                    onClick={() => deleteBatch(b)}
                    className="text-[var(--err)] hover:opacity-70 disabled:opacity-30"
                    title="Admin: permanently delete this upload and its transactions"
                  >
                    <Trash2 size={10} />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <TxTable txs={txs} coa={coa} onReload={onReload} locked={locked} isAdmin={isAdmin} job={job} onConfirmChange={onReload} />

      {uploadDlg && (
        <UploadCSVDialog
          initial={uploadDlg}
          busy={!!busy}
          onCancel={() => setUploadDlg(null)}
          onConfirm={(file, dateFrom, dateTo) => uploadFile(file, dateFrom, dateTo)}
        />
      )}
    </div>
  );
}

function UploadCSVDialog({ initial, busy, onCancel, onConfirm }) {
  const [file, setFile] = useState(initial.file || null);
  const [dateFrom, setDateFrom] = useState(initial.dateFrom || "");
  const [dateTo, setDateTo] = useState(initial.dateTo || "");
  const [rangeOn, setRangeOn] = useState(!!(initial.dateFrom || initial.dateTo));
  const fileRef = useRef(null);
  useEffect(() => { if (!file) fileRef.current?.click(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="upload-dialog">
      <div className="bg-[var(--surface)] rounded shadow-xl w-full max-w-md p-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-display text-lg flex items-center gap-2"><Upload size={16} /> Import Bank Statement</h2>
          <button onClick={onCancel} className="btn-ghost !p-1"><X size={14} /></button>
        </div>
        <p className="text-[10px] text-[var(--ink-2)] mb-3">
          Every additional column in the CSV (Summary, Reference, Memo, etc.) is captured on each row
          and shown to the AI when coding. Each upload becomes its own batch — you can archive or
          delete it later without touching earlier imports.
        </p>

        <label className="label-overline">CSV / Excel file</label>
        <input
          ref={fileRef}
          data-testid="upload-file"
          type="file"
          accept=".csv,.xlsx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="input mb-3 text-xs"
        />
        {file && <div className="text-[10px] text-[var(--ink-2)] mb-3">✓ {file.name}</div>}

        <label className="flex items-center gap-2 text-[11px] mb-2 cursor-pointer">
          <input
            data-testid="upload-range-toggle"
            type="checkbox"
            checked={rangeOn}
            onChange={(e) => setRangeOn(e.target.checked)}
          />
          Only import between specific dates
        </label>
        {rangeOn && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <label className="text-[9px] uppercase text-[var(--ink-2)] tracking-wider">From</label>
              <input data-testid="upload-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-slim text-xs !py-1 w-full" />
            </div>
            <div>
              <label className="text-[9px] uppercase text-[var(--ink-2)] tracking-wider">To</label>
              <input data-testid="upload-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-slim text-xs !py-1 w-full" />
            </div>
            <div className="col-span-2 text-[10px] text-[var(--ink-2)] italic">
              Rows outside this range will be skipped. Handy for splitting a long export into monthly
              chunks or for filling a small gap without re-uploading everything.
            </div>
          </div>
        )}

        <button
          data-testid="upload-confirm"
          disabled={!file || busy}
          onClick={() => onConfirm(file, rangeOn ? dateFrom : "", rangeOn ? dateTo : "")}
          className="btn-primary w-full text-sm !py-2 flex items-center justify-center gap-2 disabled:opacity-40"
        >
          <Upload size={13} /> {busy ? "Uploading…" : "Import"}
        </button>
      </div>
    </div>
  );
}

function CoaManager({ coa, locked, onChange }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("P&L");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function add() {
    setErr("");
    const nm = name.trim();
    if (!nm) { setErr("Enter a name."); return; }
    setBusy(true);
    try {
      await api.post("/frtx/coa", { name: nm, type });
      setName("");
      await onChange();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy(false);
  }
  async function remove(c) {
    if (!window.confirm(`Delete custom COA "${c.name}"?`)) return;
    setErr("");
    try {
      await api.delete(`/frtx/coa/${c.id}`);
      await onChange();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }

  const template = coa.filter((c) => c.origin !== "custom");
  const custom = coa.filter((c) => c.origin === "custom");

  return (
    <div className="card-flat p-4 mb-3" data-testid="coa-manager">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h4 className="font-display text-sm flex-1">Chart of Accounts — {coa.length} categories</h4>
        <input
          data-testid="coa-add-name"
          placeholder="New COA name (e.g. Crypto Fees)"
          value={name}
          disabled={locked || busy}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          className="input-slim text-xs !py-1 min-w-[220px]"
        />
        <select data-testid="coa-add-type" value={type} disabled={locked || busy} onChange={(e) => setType(e.target.value)} className="input-slim text-xs !py-1">
          <option value="P&L">P&amp;L</option>
          <option value="BS">Balance Sheet</option>
        </select>
        <button data-testid="coa-add-btn" onClick={add} disabled={locked || busy || !name.trim()} className="btn-primary text-xs flex items-center gap-2 disabled:opacity-40">
          <Plus size={11} /> Add COA
        </button>
      </div>
      {err && <div className="text-[10px] text-[var(--err)] mb-2" data-testid="coa-mgr-err">⚠ {err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
        {template.map((c) => (
          <div key={c.name} className="flex items-center justify-between border-b border-[var(--line)]/60 py-1">
            <span>{c.name}</span>
            <span className="text-[9px] text-[var(--ink-2)] uppercase tracking-wider">{c.type}</span>
          </div>
        ))}
        {custom.map((c) => (
          <div key={c.name} className="flex items-center justify-between border-b border-[var(--line)]/60 py-1 bg-[var(--navy)]/5" data-testid={`coa-row-${c.name.replace(/[^a-z0-9]+/gi, "_")}`}>
            <span className="flex items-center gap-2">
              {c.name}
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--navy)] text-white uppercase">custom</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-[var(--ink-2)] uppercase tracking-wider">{c.type}</span>
              <button data-testid={`coa-del-${c.id}`} disabled={locked} onClick={() => remove(c)} className="text-[var(--err)] disabled:opacity-40" title="Delete custom COA">
                <Trash2 size={10} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {custom.length === 0 && (
        <div className="text-[10px] text-[var(--ink-2)] italic mt-2">No custom categories yet. Template ships with {template.length} — add your own above.</div>
      )}
    </div>
  );
}

function ExtraFieldsPopover({ fields, testid }) {
  const [open, setOpen] = useState(false);
  const keys = Object.keys(fields || {});
  if (!keys.length) return null;
  return (
    <div className="relative shrink-0">
      <button
        data-testid={testid}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className="text-[var(--ink-2)] hover:text-[var(--navy)]"
        title={`${keys.length} additional column(s) from the CSV`}
      >
        <Info size={11} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-4 z-30 min-w-[240px] max-w-[380px] bg-white border border-[var(--line)] rounded shadow-lg p-2 text-[10px]"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <div className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] mb-1 border-b border-[var(--line)] pb-1">
            Extra columns from upload
          </div>
          <table className="w-full">
            <tbody>
              {keys.map((k) => (
                <tr key={k} className="align-top">
                  <td className="pr-2 py-0.5 text-[var(--ink-2)] whitespace-nowrap">{k}</td>
                  <td className="py-0.5 text-[var(--ink-1)] break-all">{fields[k]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function TxTable({ txs, coa, onReload, locked, isAdmin, job, onConfirmChange }) {
  const [search, setSearch] = useState("");
  const [coaFilter, setCoaFilter] = useState(""); // "", "__uncoded__", or an exact COA name
  const [queryOnly, setQueryOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dir, setDir] = useState(""); // "", "in", "out"
  const [showArchived, setShowArchived] = useState(false);
  const [groupByCoa, setGroupByCoa] = useState(false);
  const confirmedSet = useMemo(() => new Set(job?.coa_confirmed || []), [job?.coa_confirmed]);

  async function toggleConfirmed(name) {
    const next = new Set(confirmedSet);
    if (next.has(name)) next.delete(name); else next.add(name);
    try {
      await api.patch(`/frtx/jobs/${job.id}`, { coa_confirmed: Array.from(next) });
      onConfirmChange?.();
    } catch (e) { alert("Failed: " + formatErr(e.response?.data?.detail)); }
  }

  async function update(t, field, value) {
    if (locked) return;
    const patch = {};
    patch[field] = value;
    try { await api.patch(`/frtx/transactions/${t.id}`, patch); onReload(); }
    catch (e) { alert("Failed: " + formatErr(e.response?.data?.detail)); }
  }
  async function archiveTx(t) {
    if (!isAdmin) return;
    if (!window.confirm(`Archive this transaction?\n${t.date} · ${t.description} · £${t.amount}\n\nIt will be hidden from the ETB and totals until unarchived.`)) return;
    try { await api.post(`/frtx/transactions/${t.id}/archive`); onReload(); }
    catch (e) { alert("Failed: " + formatErr(e.response?.data?.detail)); }
  }
  async function unarchiveTx(t) {
    if (!isAdmin) return;
    try { await api.post(`/frtx/transactions/${t.id}/unarchive`); onReload(); }
    catch (e) { alert("Failed: " + formatErr(e.response?.data?.detail)); }
  }

  const coaNames = useMemo(() => new Set(coa.map(c => c.name)), [coa]);
  // Live per-COA counts on non-archived rows — used to (a) hide zero-count COAs from the
  // filter and (b) build the group-by-COA sections. Blank/uncoded rows accumulate under "".
  const coaCounts = useMemo(() => {
    const m = new Map();
    for (const t of txs) {
      if (!showArchived && t.archived) continue;
      const k = t.coa_name && coaNames.has(t.coa_name) ? t.coa_name : "";
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [txs, showArchived, coaNames]);

  // Mapped-only, sorted-by-count list used for the plain dropdown + "Next COA" walker.
  const mappedSorted = useMemo(() => {
    return coa
      .map((c) => ({ name: c.name, type: c.type, count: coaCounts.get(c.name) || 0 }))
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [coa, coaCounts]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return txs.filter(t => {
      if (!showArchived && t.archived) return false;
      if (s && !(t.description || "").toLowerCase().includes(s)) return false;
      if (dateFrom && (t.date || "") < dateFrom) return false;
      if (dateTo && (t.date || "") > dateTo) return false;
      if (dir === "in" && t.is_debit) return false;
      if (dir === "out" && !t.is_debit) return false;
      // "Uncoded" filter also matches orphans (coded to a COA no longer in the chart)
      if (coaFilter === "__uncoded__") { if (t.coa_name && coaNames.has(t.coa_name)) return false; }
      else if (coaFilter && t.coa_name !== coaFilter) return false;
      if (queryOnly && !t.is_query) return false;
      return true;
    });
  }, [txs, search, coaFilter, queryOnly, coaNames, dateFrom, dateTo, dir, showArchived]);

  const archivedCount = txs.filter((t) => t.archived).length;

  // Totals — subtotal reflects current filters, total is unconditional.
  const sumIn = (list) => list.reduce((s, t) => s + (!t.is_debit ? Number(t.amount) || 0 : 0), 0);
  const sumOut = (list) => list.reduce((s, t) => s + (t.is_debit ? Number(t.amount) || 0 : 0), 0);
  const subIn = sumIn(filtered), subOut = sumOut(filtered);
  const totIn = sumIn(txs), totOut = sumOut(txs);
  const filterActive = !!(search || coaFilter || queryOnly || dateFrom || dateTo || dir);
  const anyFilter = filterActive; // alias for readability

  function clearFilters() {
    setSearch(""); setCoaFilter(""); setQueryOnly(false);
    setDateFrom(""); setDateTo(""); setDir(""); setShowArchived(false);
  }

  // Step to the next mapped COA in the sorted list — one-click sense-check walker.
  function nextCoa() {
    if (mappedSorted.length === 0) return;
    if (!coaFilter || coaFilter === "__uncoded__") {
      setCoaFilter(mappedSorted[0].name);
      return;
    }
    const i = mappedSorted.findIndex((c) => c.name === coaFilter);
    const nxt = mappedSorted[(i + 1) % mappedSorted.length];
    setCoaFilter(nxt.name);
  }

  return (
    <div className="card-flat">
      <div className="flex items-center gap-2 px-2 py-1 border-b border-[var(--line)] flex-wrap text-[10px]">
        <button
          data-testid="tx-query-only"
          onClick={() => setQueryOnly(!queryOnly)}
          className={`px-1.5 py-0.5 rounded border ${queryOnly ? "bg-[var(--err)] text-white border-[var(--err)]" : "border-[var(--line)] text-[var(--ink-2)]"}`}
        >
          {queryOnly ? "Queries only ✓" : "Queries only"}
        </button>
        {anyFilter && (
          <button data-testid="tx-clear-filters" onClick={clearFilters} className="text-[var(--navy)] underline">
            Clear filters
          </button>
        )}
        {archivedCount > 0 && (
          <label className="flex items-center gap-1 text-[10px] text-[var(--ink-2)] cursor-pointer" data-testid="tx-show-archived-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-3 h-3" />
            Show archived <span className="font-mono-data">({archivedCount})</span>
          </label>
        )}
        <label className="flex items-center gap-1 text-[10px] text-[var(--ink-2)] cursor-pointer" data-testid="tx-group-toggle">
          <input type="checkbox" checked={groupByCoa} onChange={(e) => setGroupByCoa(e.target.checked)} className="w-3 h-3" />
          Group by COA
        </label>
        <span className="text-[var(--ink-2)] ml-auto" data-testid="tx-count">
          {filtered.length} of {txs.length} shown
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="tbl tbl-slim">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th className="text-right">In</th>
              <th className="text-right">Out</th>
              <th>Chart of Account</th>
              <th className="text-right">Conf.</th>
              <th className="text-center">?</th>
            </tr>
            <tr className="bg-[var(--bg)]" data-testid="tx-filter-row">
              <th className="!py-1 !px-1.5">
                <div className="flex items-center gap-1">
                  <input data-testid="tx-filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-slim !py-0 !px-1 text-[9px] h-5 w-[92px] leading-none" title="From date" />
                  <span className="text-[9px] text-[var(--ink-2)]">–</span>
                  <input data-testid="tx-filter-date-to"   type="date" value={dateTo}   onChange={(e) => setDateTo(e.target.value)}   className="input-slim !py-0 !px-1 text-[9px] h-5 w-[92px] leading-none" title="To date" />
                </div>
              </th>
              <th className="!py-1 !px-1.5">
                <input
                  data-testid="tx-filter-desc"
                  placeholder="Search description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-slim !py-0 !px-1.5 text-[10px] h-5 w-full leading-none"
                />
              </th>
              <th className="!py-1 !px-1.5 text-right" colSpan={2}>
                <div className="inline-flex rounded border border-[var(--line)] overflow-hidden text-[9px]" role="group" aria-label="Direction filter">
                  <button data-testid="tx-filter-dir-all" onClick={() => setDir("")}
                    className={`px-1.5 h-5 leading-none ${!dir ? "bg-[var(--navy)] text-white" : "bg-white text-[var(--ink-2)]"}`}>All</button>
                  <button data-testid="tx-filter-in" onClick={() => setDir(dir === "in" ? "" : "in")}
                    className={`px-1.5 h-5 leading-none border-l border-[var(--line)] ${dir === "in" ? "bg-[var(--ok)] text-white" : "bg-white text-[var(--ink-2)]"}`}>In</button>
                  <button data-testid="tx-filter-out" onClick={() => setDir(dir === "out" ? "" : "out")}
                    className={`px-1.5 h-5 leading-none border-l border-[var(--line)] ${dir === "out" ? "bg-[var(--err)] text-white" : "bg-white text-[var(--ink-2)]"}`}>Out</button>
                </div>
              </th>
              <th className="!py-1 !px-1.5">
                <div className="flex items-center gap-1">
                  <select
                    data-testid="tx-filter-coa"
                    value={coaFilter}
                    onChange={(e) => setCoaFilter(e.target.value)}
                    className={`input-slim !py-0 !px-1.5 text-[10px] h-5 leading-none flex-1 ${coaFilter ? "border-[var(--navy)] text-[var(--navy)] font-medium" : ""}`}
                    title="Filter transactions by Chart of Account (mapped only, sorted by count)"
                  >
                    <option value="">— All ({txs.length}) —</option>
                    <option value="__uncoded__">— Uncoded / Orphaned ({coaCounts.get("") || 0}) —</option>
                    {mappedSorted.map((c) => (
                      <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
                    ))}
                  </select>
                  <button
                    data-testid="tx-filter-next-coa"
                    onClick={nextCoa}
                    disabled={mappedSorted.length === 0}
                    className="text-[10px] h-5 px-1.5 rounded border border-[var(--line)] hover:bg-[var(--bg)] disabled:opacity-30 leading-none"
                    title="Sense-check walker: jump to the next mapped COA (in count order)"
                  >
                    ▶
                  </button>
                </div>
              </th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Group by COA when the toggle is on — inject a header tr each time the coa_name
              // changes. Rows within a group are sorted by date so the sense-check reads
              // chronologically for that category. Uncoded/orphaned rows cluster under "".
              const rowsSorted = groupByCoa
                ? [...filtered].sort((a, b) => {
                    const ka = a.coa_name && coaNames.has(a.coa_name) ? a.coa_name : "";
                    const kb = b.coa_name && coaNames.has(b.coa_name) ? b.coa_name : "";
                    if (ka === kb) return (a.date || "").localeCompare(b.date || "");
                    if (ka === "") return -1;
                    if (kb === "") return 1;
                    return ka.localeCompare(kb);
                  })
                : filtered;
              const groupTotals = new Map();
              if (groupByCoa) {
                for (const t of rowsSorted) {
                  const k = t.coa_name && coaNames.has(t.coa_name) ? t.coa_name : "";
                  const g = groupTotals.get(k) || { count: 0, in: 0, out: 0 };
                  g.count += 1;
                  if (t.is_debit) g.out += Number(t.amount) || 0;
                  else g.in += Number(t.amount) || 0;
                  groupTotals.set(k, g);
                }
              }
              const out = [];
              let prevKey = null;
              rowsSorted.forEach((t) => {
                const xf = t.extra_fields || {};
                const xfKeys = Object.keys(xf);
                const summaryKey = xfKeys.find((k) => /summary|memo|reference|note|category|type|payee|merchant/i.test(k))
                  || (xfKeys.length === 1 ? xfKeys[0] : null);
                const summary = summaryKey ? xf[summaryKey] : null;
                if (groupByCoa) {
                  const key = t.coa_name && coaNames.has(t.coa_name) ? t.coa_name : "";
                  if (key !== prevKey) {
                    const g = groupTotals.get(key);
                    const isConfirmed = key && confirmedSet.has(key);
                    out.push(
                      <tr key={`h-${key || "__uncoded__"}`} className={`border-t-2 ${isConfirmed ? "bg-[var(--ok-bg,#DCFCE7)] border-[var(--ok)]/40" : "bg-[var(--navy)]/8 border-[var(--navy)]/30"}`} data-testid={key ? `tx-group-${key.replace(/[^a-z0-9]+/gi, "_")}` : "tx-group___uncoded__"}>
                        <td colSpan={7} className="py-1 px-1.5">
                          <div className="flex items-center gap-3 text-[11px]">
                            {isConfirmed && <CheckCircle2 size={12} className="text-[var(--ok)] shrink-0" data-testid={`tx-group-tick-${key.replace(/[^a-z0-9]+/gi, "_")}`} />}
                            <span className={`font-medium ${isConfirmed ? "text-[var(--ok)]" : "text-[var(--navy)]"}`}>{key || "— Uncoded / Orphaned —"}</span>
                            <span className="text-[var(--ink-2)] font-mono-data">{g.count} tx</span>
                            <span className="text-[var(--ok)] font-mono-data">{fmt(g.in)}</span>
                            <span className="text-[var(--err)] font-mono-data">{fmt(g.out)}</span>
                            <span className="text-[var(--ink-2)] font-mono-data ml-auto mr-2">Net {fmtSigned(g.in - g.out)}</span>
                            {key ? (
                              isConfirmed ? (
                                <button
                                  data-testid={`tx-group-unconfirm-${key.replace(/[^a-z0-9]+/gi, "_")}`}
                                  onClick={() => toggleConfirmed(key)}
                                  className="text-[9px] uppercase tracking-wider text-[var(--ok)] underline"
                                  title="Undo sense-check tick for this COA"
                                >
                                  ✓ confirmed
                                </button>
                              ) : (
                                <button
                                  data-testid={`tx-group-confirm-${key.replace(/[^a-z0-9]+/gi, "_")}`}
                                  onClick={() => toggleConfirmed(key)}
                                  className="text-[10px] px-2 py-0.5 rounded border border-[var(--ok)] text-[var(--ok)] hover:bg-[var(--ok)] hover:text-white flex items-center gap-1"
                                  title="Mark this COA as sense-checked — a green tick will appear on the header and every row"
                                >
                                  <CheckCircle2 size={10} /> Confirm
                                </button>
                              )
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                    prevKey = key;
                  }
                }
                out.push(
              <tr key={t.id} data-testid={`tx-${t.id}`} className={`${t.is_query ? "bg-[var(--err-bg,#FEE2E2)] border-l-4 border-l-[var(--err)]" : ""} ${t.archived ? "opacity-45 italic" : ""}`}>
                <td className="font-mono-data text-[10px] whitespace-nowrap">
                  {t.coa_name && confirmedSet.has(t.coa_name) && (
                    <CheckCircle2 size={10} className="text-[var(--ok)] inline mr-1" data-testid={`tx-tick-${t.id}`} />
                  )}
                  {t.date}
                  {t.archived && <span className="ml-1 text-[8px] px-1 py-0.5 rounded bg-[var(--ink-2)]/20 text-[var(--ink-2)] uppercase">arch</span>}
                </td>
                <td className="text-[11px]">
                  <div className="flex items-start gap-1">
                    <div className="flex-1 min-w-0">
                      <div>{t.description}</div>
                      {summary && summary !== t.description && (
                        <div className="text-[9.5px] text-[var(--ink-2)] italic truncate" title={summary} data-testid={`tx-summary-${t.id}`}>
                          <span className="uppercase tracking-wider text-[8px] mr-1">{summaryKey}:</span>{summary}
                        </div>
                      )}
                    </div>
                    {xfKeys.length > 0 && (
                      <ExtraFieldsPopover fields={xf} testid={`tx-info-${t.id}`} />
                    )}
                  </div>
                </td>
                <td className="text-right font-mono-data text-[10px] text-[var(--ok)] whitespace-nowrap">{!t.is_debit ? fmt(t.amount) : ""}</td>
                <td className="text-right font-mono-data text-[10px] whitespace-nowrap">{t.is_debit ? fmt(t.amount) : ""}</td>
                <td>
                  <select
                    data-testid={`tx-coa-${t.id}`}
                    value={t.coa_name || ""}
                    disabled={locked}
                    onChange={(e) => update(t, "coa_name", e.target.value)}
                    className={`input-slim text-[10px] !py-0 !px-1 min-w-[180px] disabled:opacity-60 ${t.is_query ? "border-[var(--err)]" : ""}`}
                  >
                    <option value="">— select —</option>
                    {coa.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </td>
                <td className="text-right font-mono-data text-[10px] whitespace-nowrap">
                  {t.source === "memory" ? (
                    <span
                      data-testid={`tx-source-memory-${t.id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--navy)]/10 text-[var(--navy)] text-[9px] font-medium"
                      title={`Auto-coded from firm memory (${t.memory_hit_count || "?"} prior hits) — you can override if this is wrong.`}
                    >
                      MEMORY {t.confidence != null ? `${(t.confidence * 100).toFixed(0)}%` : ""}
                    </span>
                  ) : (
                    t.confidence != null ? `${(t.confidence * 100).toFixed(0)}%` : "—"
                  )}
                </td>
                <td className="text-center whitespace-nowrap">
                  <button
                    data-testid={`tx-query-${t.id}`}
                    disabled={locked}
                    onClick={() => update(t, "is_query", !t.is_query)}
                    className="text-xs"
                    title={t.is_query ? (t.query_reason || "Marked as query") : "Raise Query"}
                  >
                    {t.is_query
                      ? <AlertTriangle size={12} className="text-[var(--err)] inline" />
                      : <Circle size={12} className="text-[var(--ink-2)] inline" />}
                  </button>
                  {isAdmin && (
                    t.archived ? (
                      <button
                        data-testid={`tx-unarch-${t.id}`}
                        onClick={() => unarchiveTx(t)}
                        className="ml-1"
                        title="Restore this transaction"
                      >
                        <Unlock size={11} className="text-[var(--ok)] inline" />
                      </button>
                    ) : (
                      <button
                        data-testid={`tx-arch-${t.id}`}
                        disabled={locked}
                        onClick={() => archiveTx(t)}
                        className="ml-1 opacity-40 hover:opacity-100"
                        title="Admin: archive this transaction (hides from ETB/totals)"
                      >
                        <Lock size={11} className="text-[var(--ink-2)] inline" />
                      </button>
                    )
                  )}
                </td>
              </tr>
                );
              });
              return out;
            })()}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[var(--ink-2)] text-xs">
              {txs.length === 0 ? "No transactions. Upload a bank CSV to start." : "No transactions match the current filters."}
            </td></tr>}
          </tbody>
          <tfoot>
            <tr className="font-medium bg-[var(--bg)]" data-testid="tx-subtotal">
              <td colSpan={2} className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">
                {anyFilter ? `Subtotal (${filtered.length} filtered)` : `Total (${txs.length} rows)`}
              </td>
              <td className="text-right font-mono-data text-[10px] text-[var(--ok)]">{fmt(subIn)}</td>
              <td className="text-right font-mono-data text-[10px]">{fmt(subOut)}</td>
              <td colSpan={3} className="text-right font-mono-data text-[10px] text-[var(--ink-2)]">
                Net: {fmtSigned(subIn - subOut)}
              </td>
            </tr>
            {anyFilter && (
              <tr className="font-medium bg-[var(--bg)] border-t border-[var(--line)]" data-testid="tx-total">
                <td colSpan={2} className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">Total (all rows)</td>
                <td className="text-right font-mono-data text-[10px] text-[var(--ok)]">{fmt(totIn)}</td>
                <td className="text-right font-mono-data text-[10px]">{fmt(totOut)}</td>
                <td colSpan={3} className="text-right font-mono-data text-[10px] text-[var(--ink-2)]">
                  Net: {fmtSigned(totIn - totOut)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ==================================================================== Stage 3: Query Management */

function QueriesTab({ job, onReload, onNext }) {
  const [data, setData] = useState({ queries: [], email: null });
  const [coa, setCoa] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const locked = job.status === "approved";

  async function load() {
    const [q, c] = await Promise.all([api.get(`/frtx/jobs/${job.id}/queries`), api.get("/frtx/coa")]);
    setData(q.data); setCoa(c.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job.id]);

  async function draft() {
    setErr(""); setBusy("Drafting email with Claude Sonnet 4.5…");
    try { await api.post(`/frtx/jobs/${job.id}/queries/draft-email`); await load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy("");
  }
  async function updateEmail(patch) {
    try { await api.patch(`/frtx/jobs/${job.id}/queries/email`, patch); await load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  async function resolve(t, coaName) {
    if (!coaName) return;
    try { await api.post(`/frtx/jobs/${job.id}/queries/resolve`, { transaction_id: t.id, coa_name: coaName }); await load(); await onReload(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  async function resolveBulk(txs, coaName) {
    if (!coaName || !txs.length) return;
    try {
      const r = await api.post(`/frtx/jobs/${job.id}/queries/resolve-bulk`, { transaction_ids: txs.map(t => t.id), coa_name: coaName });
      await load(); await onReload();
      setErr("");
      return r.data;
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  async function complete() {
    try { await api.post(`/frtx/jobs/${job.id}/advance?to=etb`); await onReload(); onNext(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  async function copyText(kind, txt) {
    try { await navigator.clipboard.writeText(txt); setCopied(kind); setTimeout(() => setCopied(""), 2000); }
    catch { window.prompt("Copy this:", txt); }
  }

  const email = data.email;
  const statusPill = (s) => {
    const map = {
      open: "border-[var(--line)] text-[var(--ink-2)]",
      sent: "border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]",
      responded: "border-[var(--navy)] text-[var(--navy)] bg-[var(--navy-soft)]",
      resolved: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]",
    };
    return <span className={`status-pill text-[10px] ${map[s] || "border-[var(--line)]"}`}>{s}</span>;
  };

  // Group queries by merchant / description signature so accountants can bulk-resolve.
  // Signature: uppercase, strip digits + punctuation + common location tails, keep first 2 words.
  function signature(desc) {
    if (!desc) return "";
    let s = String(desc).toUpperCase();
    s = s.replace(/[0-9]+/g, " ").replace(/[^A-Z\s]/g, " ").replace(/\s+/g, " ").trim();
    const words = s.split(" ").filter(w => w.length > 2);
    return words.slice(0, 2).join(" ") || (words[0] || s);
  }
  const groups = useMemo(() => {
    const m = new Map();
    for (const t of data.queries) {
      const sig = signature(t.description);
      if (!m.has(sig)) m.set(sig, { sig, sample: t.description, txs: [], suggested: t.coa_name });
      m.get(sig).txs.push(t);
    }
    // Sort largest groups first — biggest bulk resolve wins go up top
    return Array.from(m.values()).sort((a, b) => b.txs.length - a.txs.length);
  }, [data.queries]);

  return (
    <div>
      <div className="card-flat p-5 mb-4">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-display text-xl flex items-center gap-2"><HelpCircle size={18} /> Query Management</h2>
          {email && statusPill(email.status)}
        </div>
        <p className="text-xs text-[var(--ink-2)] mb-4">One consolidated email to the client covering every open query. AI drafts it — you paste it into your inbox and record the response here.</p>

        {!email && (
          <button data-testid="q-draft" disabled={!data.queries.length || busy || locked} onClick={draft} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
            <Sparkles size={13} /> {busy || `Draft Consolidated Email (${data.queries.length} ${data.queries.length === 1 ? "query" : "queries"})`}
          </button>
        )}
        {!data.queries.length && !email && <div className="text-xs text-[var(--ink-2)] italic mt-2">No open queries — either code every transaction or advance to ETB.</div>}

        {email && (
          <div className="space-y-3">
            <div>
              <label className="label-overline flex items-center justify-between">
                <span>Subject</span>
                <button onClick={() => copyText("subj", email.subject)} className="btn-ghost !py-0.5 !px-2 text-[10px] flex items-center gap-1"><Copy size={10} /> {copied === "subj" ? "Copied" : "Copy"}</button>
              </label>
              <input data-testid="q-email-subject" value={email.subject || ""} onChange={(e) => updateEmail({ subject: e.target.value })} disabled={locked} className="input text-sm" />
            </div>
            <div>
              <label className="label-overline flex items-center justify-between">
                <span>Body</span>
                <button onClick={() => copyText("body", email.body)} className="btn-ghost !py-0.5 !px-2 text-[10px] flex items-center gap-1"><Copy size={10} /> {copied === "body" ? "Copied" : "Copy"}</button>
              </label>
              <textarea data-testid="q-email-body" value={email.body || ""} onChange={(e) => updateEmail({ body: e.target.value })} disabled={locked} className="input text-sm h-56 font-mono-data" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="label-overline">Status:</label>
              {["open", "sent", "responded", "resolved"].map(s => (
                <button
                  key={s}
                  data-testid={`q-status-${s}`}
                  disabled={locked}
                  onClick={() => updateEmail({ status: s })}
                  className={`px-2 py-1 rounded text-[10px] border transition ${email.status === s ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--bg)]"}`}
                >
                  {s}
                </button>
              ))}
              <button onClick={draft} disabled={busy || locked} className="btn-ghost text-[10px] flex items-center gap-1 ml-auto"><Sparkles size={10} /> Re-draft</button>
            </div>
            {(email.status === "responded" || email.status === "resolved") && (
              <div>
                <label className="label-overline">Client Response (paste here)</label>
                <textarea data-testid="q-response" value={email.response_text || ""} onChange={(e) => updateEmail({ response_text: e.target.value })} disabled={locked} className="input text-xs h-24" />
              </div>
            )}
          </div>
        )}
        {err && <div className="text-xs text-[var(--err)] mt-3" data-testid="q-err">{err}</div>}
      </div>

      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base">Open Queries ({data.queries.length} · {groups.length} groups)</h3>
          <button data-testid="q-complete" disabled={locked || data.queries.length > 0} onClick={complete} className="btn-ghost text-xs flex items-center gap-2 disabled:opacity-40" title={data.queries.length ? "Resolve every query first" : ""}>
            Queries Complete <ArrowRight size={12} />
          </button>
        </div>
        {data.queries.length === 0 && <div className="text-xs text-[var(--ink-2)] italic">Nothing outstanding. You can advance to ETB.</div>}
        <div className="space-y-2">
          {groups.map((g) => <QueryGroup key={g.sig} group={g} coa={coa} locked={locked} resolve={resolve} resolveBulk={resolveBulk} />)}
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== Stage 4: ETB */

function ETBTab({ job, onReload, onNext }) {
  const [etb, setEtb] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [existingEtb, setExistingEtb] = useState(job.existing_etb || false);
  const [openingPanel, setOpeningPanel] = useState(null); // { rows:[{name,value}], matched, unmatched }
  const [subTab, setSubTab] = useState("etb"); // "etb" | "adj"
  const [showEmpty, setShowEmpty] = useState(false);
  const [coa, setCoa] = useState([]);
  const [journals, setJournals] = useState([]);
  const priorRef = useRef(null);
  const locked = job.status === "approved";

  useEffect(() => { setExistingEtb(job.existing_etb || false); }, [job.existing_etb]);
  // Refetch COA whenever the sub-tab changes so custom categories added on the COA tab
  // (via Manage COA) appear here without the user having to reload the whole page.
  useEffect(() => { api.get("/frtx/coa").then(r => setCoa(r.data)); }, [subTab]);

  async function toggleExisting(v) {
    setExistingEtb(v);
    await api.patch(`/frtx/jobs/${job.id}`, { existing_etb: v });
    if (!v) {
      // Clear opening balances when toggled off
      await api.patch(`/frtx/jobs/${job.id}`, { /* keep server logic; UI just refreshes */ });
    }
    await onReload();
    await load();
  }
  async function openManualEntry(extra) {
    setErr("");
    try {
      const r = await api.get(`/frtx/jobs/${job.id}/bs-categories`);
      setOpeningPanel({ rows: r.data.categories, ...(extra || {}) });
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  async function saveOpeningBalances(rows) {
    setBusy("Saving opening balances…"); setErr("");
    try {
      const balances = {};
      rows.forEach((r) => { balances[r.name] = Number(r.value) || 0; });
      const j = await api.put(`/frtx/jobs/${job.id}/opening-balances`, { balances });
      const imb = Number(j.data.imbalance || 0);
      if (Math.abs(imb) >= 0.01) {
        setErr(`⚠ Opening balances saved (${j.data.non_zero} non-zero) but the Balance Sheet doesn't reconcile — total ${fmtSigned(imb)} instead of £0. Reopen "Enter Manually" to correct.`);
      } else {
        setErr(`✓ Opening balances saved — ${j.data.non_zero} of ${j.data.accounts} Balance Sheet items non-zero, reconciled to £0.`);
      }
      setOpeningPanel(null);
      await onReload();
      await load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy("");
  }
  async function uploadPriorEtb(file) {
    setErr(""); setBusy("Uploading prior ETB…");
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await fetch(`${API}/frtx/jobs/${job.id}/upload-prior-etb`, { method: "POST", body: fd, credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail || j));
      const matched = j.matched || [];
      const unmatched = j.unmatched || [];
      // Reload categories with parsed values and open the review panel so the user can adjust.
      const cats = await api.get(`/frtx/jobs/${job.id}/bs-categories`);
      setOpeningPanel({ rows: cats.data.categories, matched, unmatched, uploaded: file.name });
      if ((j.non_zero || 0) === 0) {
        setErr("Prior ETB parsed but no Balance Sheet figures were auto-matched — review and enter below, then Save.");
      } else {
        setErr(`✓ Parsed ${matched.length} row(s), ${j.non_zero} with values. Review and edit below, then Save.`);
      }
      await onReload();
      await load();
    } catch (e) { setErr(e.message); }
    setBusy("");
  }

  async function load() {
    const [e, jr] = await Promise.all([
      api.get(`/frtx/jobs/${job.id}/etb`),
      api.get(`/frtx/jobs/${job.id}/journals`),
    ]);
    setEtb(e.data);
    setJournals(jr.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job.id]);

  async function saveJournal(entry) {
    setErr("");
    try {
      await api.post(`/frtx/jobs/${job.id}/journals`, entry);
      await load();
      return true;
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); return false; }
  }
  async function deleteJournal(eid) {
    if (!window.confirm("Delete this journal entry?")) return;
    setErr("");
    try {
      await api.delete(`/frtx/jobs/${job.id}/journals/${eid}`);
      await load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  async function complete() {
    try { await api.post(`/frtx/jobs/${job.id}/advance?to=review`); await onReload(); onNext(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  }
  async function exportEtb() {
    setErr(""); setBusy("Preparing ETB.xlsx…");
    try {
      const r = await fetch(`${API}/frtx/jobs/${job.id}/export/etb`, { credentials: "include" });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try { const j = await r.json(); detail = j.detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      const blob = await r.blob();
      const slug = (job.client_name || "client").replace(/[^A-Za-z0-9]+/g, "_");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `FRTX_ETB_${slug}_${job.year_end_date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setErr(e.message); }
    setBusy("");
  }

  if (!etb) return <div className="text-sm text-[var(--ink-2)]">Loading ETB…</div>;
  const bad = Math.abs(etb.imbalance) >= 0.01;

  return (
    <div>
      <div className="card-flat p-4 mb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[var(--ink-2)]">Existing ETB?</span>
            <button
              data-testid="etb-existing-toggle"
              disabled={locked}
              onClick={() => toggleExisting(!existingEtb)}
              className={`w-9 h-5 rounded-full transition ${existingEtb ? "bg-[var(--navy)]" : "bg-[var(--line)]"} relative`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${existingEtb ? "translate-x-4" : ""}`} />
            </button>
            <span className="text-[10px] text-[var(--ink-2)] ml-2">
              {existingEtb ? "Upload last year's ETB — Balance Sheet closing balances become this year's opening." : "First-time client — opening balances default to £0."}
            </span>
          </div>
          {existingEtb && (
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="etb-prior-upload" disabled={locked || !!busy} onClick={() => priorRef.current?.click()} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-2 disabled:opacity-40">
                <Upload size={11} /> Upload Prior ETB
              </button>
              <button data-testid="etb-opening-manual" disabled={locked || !!busy} onClick={() => openManualEntry()} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-2 disabled:opacity-40">
                <Wand2 size={11} /> Enter Manually
              </button>
              <input ref={priorRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPriorEtb(e.target.files[0])} />
              {job.prior_etb_filename && <span className="text-[10px] text-[var(--ink-2)]">✓ {job.prior_etb_filename}</span>}
              {Math.abs(Number(job.opening_balances_imbalance || 0)) >= 0.01 && (
                <span data-testid="etb-opening-imbalance-chip" className="text-[10px] px-2 py-0.5 rounded-full border border-[var(--err)]/40 bg-[var(--err)]/5 text-[var(--err)] flex items-center gap-1" title="Opening balances don't reconcile to £0. Reopen Enter Manually to correct.">
                  <AlertTriangle size={10} /> Opening TB {fmtSigned(Number(job.opening_balances_imbalance))} (should be £0)
                </span>
              )}
            </div>
          )}
        </div>
        {busy && <div className="text-[10px] text-[var(--ink-2)] animate-pulse mt-2">{busy}</div>}
        {err && <div className={`text-[10px] mt-2 ${err.startsWith("✓") ? "text-[var(--ok)]" : err.startsWith("⚠") ? "text-[var(--warn,#B45309)]" : "text-[var(--err)]"}`} data-testid="etb-prior-msg">{err}</div>}
      </div>

      {openingPanel && (
        <OpeningBalancesPanel
          initial={openingPanel}
          locked={locked}
          onCancel={() => setOpeningPanel(null)}
          onSave={saveOpeningBalances}
        />
      )}
      <div className="card-flat p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1 bg-[var(--bg)] rounded-lg p-1" data-testid="etb-subtabs">
            <button data-testid="etb-subtab-etb" onClick={() => setSubTab("etb")} className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-2 ${subTab === "etb" ? "bg-white shadow-sm text-[var(--navy)] font-medium" : "text-[var(--ink-2)]"}`}>
              <TableIcon size={12} /> ETB
            </button>
            <button data-testid="etb-subtab-adj" onClick={() => setSubTab("adj")} className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-2 ${subTab === "adj" ? "bg-white shadow-sm text-[var(--navy)] font-medium" : "text-[var(--ink-2)]"}`}>
              <Sparkles size={12} /> Adjustments {journals.length > 0 && <span className="text-[9px] bg-[var(--navy)] text-white rounded-full px-1.5">{journals.length}</span>}
            </button>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {subTab === "etb" && (
              <label className="flex items-center gap-1 text-[10px] text-[var(--ink-2)] cursor-pointer" data-testid="etb-show-empty-toggle">
                <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} className="w-3 h-3" />
                Show empty COAs
              </label>
            )}
            <button data-testid="etb-export" disabled={!!busy} onClick={exportEtb} className="btn-ghost text-xs flex items-center gap-2 disabled:opacity-40">
              <Download size={12} /> Export ETB (.xlsx)
            </button>
            <button data-testid="etb-complete" disabled={locked} onClick={complete} className="btn-primary text-xs flex items-center gap-2 disabled:opacity-40">
              ETB Complete <ArrowRight size={12} />
            </button>
          </div>
        </div>
        <p className="text-xs text-[var(--ink-2)] mt-2">
          {subTab === "etb"
            ? "Auto-populated from coded transactions. The Adjustment column is driven by journal entries — switch to the Adjustments tab to add/remove them."
            : "Post balanced Dr / Cr journal entries. Each posting flows into the Adjustment column of the ETB automatically."}
        </p>
        {busy && <div className="text-xs text-[var(--ink-2)] animate-pulse mt-2">{busy}</div>}
        {bad && subTab === "etb" && (
          <div className="text-xs text-[var(--err)] mt-2 flex items-center gap-2" data-testid="etb-imbalance">
            <AlertTriangle size={12} /> Imbalance: {fmtSigned(etb.imbalance)} — add journal entries (e.g. Retained Earnings) to balance.
          </div>
        )}
      </div>

      {subTab === "adj" ? (
        <AdjustmentsPanel coa={coa} journals={journals} locked={locked} onSave={saveJournal} onDelete={deleteJournal} />
      ) : (
      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-slim">
          <thead><tr>
            <th>COA</th>
            <th>Type</th>
            <th className="text-right">Opening</th>
            <th className="text-right">COA Total</th>
            <th className="text-right">Trial Balance</th>
            <th className="text-right">Adjustment</th>
            <th className="text-right">Adjusted TB</th>
            <th className="text-right">P&amp;L</th>
            <th className="text-right">Balance Sheet</th>
          </tr></thead>
          <tbody>
            {etb.rows.filter(r => showEmpty || r.has_activity).map((r) => (
              <tr key={r.name} data-testid={`etb-${r.name.replace(/[^a-z0-9]+/gi, "_")}`}>
                <td className="text-[11px] font-medium">
                  {r.name}
                  {r.origin === "custom" && <span className="ml-1 text-[8px] px-1 py-0.5 rounded bg-[var(--navy)] text-white uppercase">custom</span>}
                </td>
                <td className="text-[9px] text-[var(--ink-2)]">{r.type}</td>
                <td className="text-right font-mono-data text-[10px] text-[var(--ink-2)]">{fmtSigned(r.opening_balance)}</td>
                <td className="text-right font-mono-data text-[10px]">{fmtSigned(r.coa_total)}</td>
                <td className="text-right font-mono-data text-[10px]">{fmtSigned(r.trial_balance)}</td>
                <td className="text-right font-mono-data text-[10px]" data-testid={`etb-adj-${r.name.replace(/[^a-z0-9]+/gi, "_")}`}>
                  {r.adjustment ? (
                    <button onClick={() => setSubTab("adj")} className="text-[var(--navy)] hover:underline" title="Click to view journals">{fmtSigned(r.adjustment)}</button>
                  ) : "—"}
                </td>
                <td className="text-right font-mono-data text-[10px] font-medium">{fmtSigned(r.adjusted_trial_balance)}</td>
                <td className="text-right font-mono-data text-[10px]">{r.type === "P&L" ? fmtSigned(r.pl) : ""}</td>
                <td className="text-right font-mono-data text-[10px]">{r.type === "BS" ? fmtSigned(r.bs) : ""}</td>
              </tr>
            ))}
            {etb.rows.filter(r => showEmpty || r.has_activity).length === 0 && (
              <tr><td colSpan={9} className="text-center py-6 text-[var(--ink-2)] text-xs italic">No COAs with activity yet. Tick &quot;Show empty COAs&quot; to see the full chart.</td></tr>
            )}
            <tr className="font-medium bg-[var(--bg)]">
              <td colSpan={2}>TOTALS</td>
              <td className="text-right font-mono-data text-[10px]">{fmtSigned(etb.totals.opening_balance)}</td>
              <td className="text-right font-mono-data text-[10px]">{fmtSigned(etb.totals.coa_total)}</td>
              <td className="text-right font-mono-data text-[10px]">{fmtSigned(etb.totals.trial_balance)}</td>
              <td className="text-right font-mono-data text-[10px]">{fmtSigned(etb.totals.adjustment)}</td>
              <td className="text-right font-mono-data text-[10px]">{fmtSigned(etb.totals.adjusted_trial_balance)}</td>
              <td className={`text-right font-mono-data text-[10px] ${bad ? "text-[var(--err)]" : ""}`}>{fmtSigned(etb.totals.pl)}</td>
              <td className={`text-right font-mono-data text-[10px] ${bad ? "text-[var(--err)]" : ""}`}>{fmtSigned(etb.totals.bs)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function AdjustmentsPanel({ coa, journals, locked, onSave, onDelete }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [memo, setMemo] = useState("");
  const [debit, setDebit] = useState("");
  const [credit, setCredit] = useState("");
  const [amount, setAmount] = useState("");
  const [posting, setPosting] = useState(false);
  const valid = date && debit && credit && debit !== credit && Number(amount) > 0;

  async function post() {
    setPosting(true);
    const ok = await onSave({ date, memo, debit_coa: debit, credit_coa: credit, amount: Number(amount) });
    setPosting(false);
    if (ok) { setMemo(""); setDebit(""); setCredit(""); setAmount(""); setDate(today); }
  }

  const totalDr = journals.reduce((s, e) => s + e.amount, 0);
  const totalCr = totalDr; // by construction

  return (
    <div className="space-y-3" data-testid="adjustments-panel">
      <div className="card-flat p-4">
        <h3 className="font-display text-sm mb-3 flex items-center gap-2"><Plus size={13} /> New Adjustment Entry</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div>
            <label className="text-[9px] uppercase text-[var(--ink-2)] tracking-wider">Date</label>
            <input data-testid="adj-date" type="date" value={date} disabled={locked} onChange={(e) => setDate(e.target.value)} className="input-slim text-xs !py-1 w-full" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[9px] uppercase text-[var(--ink-2)] tracking-wider">Debit (Dr)</label>
            <select data-testid="adj-debit" value={debit} disabled={locked} onChange={(e) => setDebit(e.target.value)} className="input-slim text-xs !py-1 w-full">
              <option value="">— select —</option>
              {coa.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[9px] uppercase text-[var(--ink-2)] tracking-wider">Credit (Cr)</label>
            <select data-testid="adj-credit" value={credit} disabled={locked} onChange={(e) => setCredit(e.target.value)} className="input-slim text-xs !py-1 w-full">
              <option value="">— select —</option>
              {coa.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase text-[var(--ink-2)] tracking-wider">Amount (£)</label>
            <input data-testid="adj-amount" type="number" step="0.01" min="0.01" placeholder="0.00" value={amount} disabled={locked} onChange={(e) => setAmount(e.target.value)} className="input-slim text-xs !py-1 w-full text-right font-mono-data" />
          </div>
          <div className="md:col-span-5">
            <label className="text-[9px] uppercase text-[var(--ink-2)] tracking-wider">Memo (optional)</label>
            <input data-testid="adj-memo" placeholder="Description — e.g. Depreciation charge for the year" value={memo} disabled={locked} onChange={(e) => setMemo(e.target.value)} className="input-slim text-xs !py-1 w-full" />
          </div>
          <button data-testid="adj-post" disabled={!valid || posting || locked} onClick={post} className="btn-primary text-xs flex items-center gap-2 disabled:opacity-40 h-[30px]">
            <CheckCircle2 size={12} /> Add to ETB
          </button>
        </div>
        {debit && credit && debit === credit && (
          <div className="text-[10px] text-[var(--err)] mt-2">Debit and Credit accounts must differ.</div>
        )}
      </div>

      <div className="card-flat overflow-x-auto">
        <div className="flex items-center justify-between p-3 border-b border-[var(--line)]">
          <div className="text-xs font-medium">Posted Journal Entries · {journals.length}</div>
          <div className="text-[10px] text-[var(--ink-2)] font-mono-data">Σ Dr = Σ Cr = <span className="text-[var(--ink-1)]">{fmtSigned(totalDr)}</span></div>
        </div>
        <table className="tbl tbl-slim">
          <thead><tr>
            <th>Date</th>
            <th>Memo</th>
            <th>Debit (Dr)</th>
            <th>Credit (Cr)</th>
            <th className="text-right">Amount</th>
            <th className="text-center">Actions</th>
          </tr></thead>
          <tbody>
            {journals.map((e) => (
              <tr key={e.id} data-testid={`adj-row-${e.id}`}>
                <td className="font-mono-data text-[10px] whitespace-nowrap">{e.date}</td>
                <td className="text-[11px]">{e.memo || <span className="text-[var(--ink-2)] italic">—</span>}</td>
                <td className="text-[11px]">{e.debit_coa}</td>
                <td className="text-[11px]">{e.credit_coa}</td>
                <td className="text-right font-mono-data text-[10px]">{fmtSigned(e.amount)}</td>
                <td className="text-center">
                  <button data-testid={`adj-del-${e.id}`} disabled={locked} onClick={() => onDelete(e.id)} className="text-[var(--err)] disabled:opacity-40" title="Delete this entry">
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
            {journals.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[var(--ink-2)] text-xs italic">No adjustments posted yet. Add your first entry above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OpeningBalancesPanel({ initial, locked, onCancel, onSave }) {
  const [rows, setRows] = useState(() => (initial.rows || []).map((r) => ({ ...r, value: r.value ?? 0 })));
  const total = useMemo(() => rows.reduce((s, r) => s + (Number(r.value) || 0), 0), [rows]);
  const nonZero = rows.filter((r) => Number(r.value)).length;
  const matched = initial.matched || [];
  const unmatched = (initial.unmatched || []).filter((u) => u.value);
  // A properly-signed opening TB must sum to zero (debits = credits). Assets are entered
  // positive, liabilities/equity as negative. Anything > 1p from zero is a soft warning.
  const imbalanced = Math.abs(total) >= 0.01 && nonZero > 0;

  function set(name, v) {
    setRows((rs) => rs.map((r) => (r.name === name ? { ...r, value: v } : r)));
  }

  return (
    <div className="card-flat p-5 mb-4" data-testid="opening-balances-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-display text-base flex items-center gap-2"><Wand2 size={14} /> Opening Balances — Balance Sheet</h3>
          <p className="text-[11px] text-[var(--ink-2)] mt-1 max-w-xl">
            {initial.uploaded
              ? `Parsed "${initial.uploaded}". ${matched.length} row(s) auto-matched to this year's chart. Review the figures below and edit any that look off, then Save.`
              : "Enter the closing balance from the prior year for each Balance Sheet item. Leave blank for £0."}
            {" "}All {rows.length} Balance Sheet categories (template + custom) carry forward — P&amp;L items reset each year.
            {" "}<span className="italic">Enter assets as positive; liabilities, capital and retained earnings as negative — the total should sum to £0.</span>
          </p>
        </div>
        <div className="text-[10px] text-[var(--ink-2)] text-right">
          <div><span className="text-[var(--ink-1)] font-medium">{nonZero}</span> non-zero</div>
          <div>Total <span className={`font-mono-data ${imbalanced ? "text-[var(--err)] font-medium" : ""}`} data-testid="opening-total">{fmtSigned(total)}</span></div>
        </div>
      </div>

      {imbalanced && (
        <div className="text-[11px] mb-3 p-2 rounded border border-[var(--err)]/40 bg-[var(--err)]/5 text-[var(--err)] flex items-center gap-2" data-testid="opening-imbalance">
          <AlertTriangle size={12} className="shrink-0" />
          <span>
            <span className="font-medium">Balance Sheet doesn&apos;t reconcile</span> — opening balances sum to <span className="font-mono-data">{fmtSigned(total)}</span> instead of £0.
            Check that liabilities, capital and retained earnings are entered as <span className="font-mono-data">negative</span> values (credits).
            You can still save — this is a heads-up, not a blocker.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0 mb-3">
        {/* Column headers — mirror the two side-by-side data columns so headings line up with values below */}
        <div className="hidden sm:grid grid-cols-[1fr_7rem] items-center gap-2 py-1.5 border-b border-[var(--line)] bg-[var(--bg)]/40">
          <span className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] font-medium">Balance Sheet Item</span>
          <span className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] font-medium text-right pr-1">Opening £</span>
        </div>
        <div className="hidden sm:grid grid-cols-[1fr_7rem] items-center gap-2 py-1.5 border-b border-[var(--line)] bg-[var(--bg)]/40">
          <span className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] font-medium">Balance Sheet Item</span>
          <span className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] font-medium text-right pr-1">Opening £</span>
        </div>
        {/* Mobile single-column header */}
        <div className="sm:hidden grid grid-cols-[1fr_7rem] items-center gap-2 py-1.5 border-b border-[var(--line)] bg-[var(--bg)]/40">
          <span className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] font-medium">Balance Sheet Item</span>
          <span className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] font-medium text-right pr-1">Opening £</span>
        </div>
        {rows.map((r) => (
          <div key={r.name} className="grid grid-cols-[1fr_7rem] items-center gap-2 py-1 border-b border-[var(--line)]/60">
            <label className="text-[11px] text-[var(--ink-1)] truncate" title={r.name}>{r.name}</label>
            <input
              data-testid={`opening-input-${r.name.replace(/[^a-z0-9]+/gi, "_")}`}
              type="number"
              step="0.01"
              value={r.value === 0 ? "" : r.value}
              disabled={locked}
              onChange={(e) => set(r.name, e.target.value === "" ? 0 : Number(e.target.value))}
              className="input-slim !py-0 !px-1 text-right font-mono-data text-[11px] w-full disabled:opacity-60"
              placeholder="0.00"
            />
          </div>
        ))}
      </div>

      {unmatched.length > 0 && (
        <div className="text-[10px] text-[var(--ink-2)] mb-3 p-2 rounded bg-[var(--bg)] border border-[var(--line)]" data-testid="opening-unmatched">
          <div className="font-medium mb-1">{unmatched.length} row(s) in your file couldn&apos;t be auto-matched — add them into the closest BS category above:</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            {unmatched.slice(0, 20).map((u, i) => (
              <div key={i} className="flex justify-between font-mono-data">
                <span className="truncate pr-2">{u.file_name}</span>
                <span>{fmtSigned(u.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 justify-end">
        <button data-testid="opening-cancel" onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
        <button data-testid="opening-save" disabled={locked} onClick={() => onSave(rows)} className={`btn-primary text-xs flex items-center gap-2 disabled:opacity-40 ${imbalanced ? "!bg-[var(--err)] hover:!bg-[var(--err)]/90" : ""}`}>
          <CheckCircle2 size={12} /> {imbalanced ? "Save Anyway" : "Save Opening Balances"}
        </button>
      </div>
    </div>
  );
}

/* ==================================================================== Stage 5: Review & Approve */

function ReviewTab({ job, readiness, onReload }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const isApproved = job.status === "approved";
  const checks = readiness?.checks || [];

  async function approve() {
    if (!window.confirm("Approve this engagement and lock all data?")) return;
    setErr(""); setBusy("Approving…");
    try { await api.post(`/frtx/jobs/${job.id}/approve`); await onReload(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy("");
  }
  async function reopen() {
    if (!window.confirm("Re-open the engagement for editing?")) return;
    setErr(""); setBusy("Re-opening…");
    try { await api.post(`/frtx/jobs/${job.id}/reopen`); await onReload(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setBusy("");
  }
  async function download(url, fname) {
    setErr(""); setBusy(`Preparing ${fname}…`);
    try {
      const r = await fetch(`${API}${url}`, { credentials: "include" });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try { const j = await r.json(); detail = j.detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setErr(e.message); }
    setBusy("");
  }

  const slug = (job.client_name || "client").replace(/[^A-Za-z0-9]+/g, "_");
  return (
    <div className="max-w-3xl">
      <div className="card-flat p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl flex items-center gap-2"><ShieldCheck size={18} /> Review &amp; Approve</h2>
          {isApproved
            ? <span className="status-pill text-[10px] border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)] flex items-center gap-1"><Lock size={10} /> Approved</span>
            : <span className="status-pill text-[10px] border-[var(--line)] text-[var(--ink-2)]">Draft</span>}
        </div>
        <p className="text-xs text-[var(--ink-2)] mb-4">When every check is green, approve to lock the engagement and unlock the file exports.</p>

        <div className="space-y-2 mb-4" data-testid="review-checks">
          {checks.map(c => (
            <div key={c.key} data-testid={`review-check-${c.key}`} className="flex items-center gap-2 text-sm">
              {c.ok ? <CheckCircle2 size={14} className="text-[var(--ok)]" /> : <AlertTriangle size={14} className="text-[var(--warn)]" />}
              <span className={c.ok ? "" : "text-[var(--warn)]"}>{c.label}</span>
            </div>
          ))}
          {!checks.length && <div className="text-xs text-[var(--ink-2)] italic">Loading readiness…</div>}
        </div>

        {err && <div className="text-xs text-[var(--err)] mb-3" data-testid="review-err">{err}</div>}
        {busy && <div className="text-xs text-[var(--ink-2)] animate-pulse mb-3">{busy}</div>}

        {!isApproved && (
          <button data-testid="review-approve" disabled={!readiness?.all_ok || !!busy} onClick={approve} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
            <Lock size={13} /> Approve &amp; Lock Engagement
          </button>
        )}
        {isApproved && (
          <div className="flex gap-2 items-center flex-wrap">
            <button data-testid="review-reopen" disabled={!!busy} onClick={reopen} className="btn-ghost text-sm flex items-center gap-2"><Unlock size={13} /> Re-open for Editing</button>
            <div className="text-xs text-[var(--ink-2)]">
              Approved by <b className="text-[var(--ink)]">{job.approved_by_name || "—"}</b> · {(job.approved_at || "").slice(0, 16).replace("T", " ")}
            </div>
          </div>
        )}
      </div>

      <div className="card-flat p-5">
        <h3 className="font-display text-lg mb-1">Exports</h3>
        <p className="text-xs text-[var(--ink-2)] mb-4">Downloads unlock after approval.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

          <div className="border border-[var(--line)] rounded p-3 flex flex-col">
            <div className="flex items-center gap-2 mb-2"><FileSpreadsheet size={16} className="text-[var(--ok)]" /><span className="font-medium text-sm">ETB (Excel)</span></div>
            <p className="text-xs text-[var(--ink-2)] flex-1 mb-3">Firm ETB template with Opening, COA Totals and Adjustments populated.</p>
            <button
              data-testid="dl-etb"
              disabled={!isApproved || !!busy}
              onClick={() => download(`/frtx/jobs/${job.id}/export/etb`, `FRTX_ETB_${slug}_${job.year_end_date}.xlsx`)}
              className="btn-primary text-xs flex items-center gap-2 justify-center disabled:opacity-40"
            >
              <Download size={12} /> Download .xlsx
            </button>
          </div>

          <div className="border border-dashed border-[var(--line)] rounded p-3 flex flex-col opacity-70">
            <div className="flex items-center gap-2 mb-2"><FileSpreadsheet size={16} className="text-[var(--ink-2)]" /><span className="font-medium text-sm">Draft Accounts</span></div>
            <p className="text-xs text-[var(--ink-2)] flex-1 mb-3">Statutory pack (Balance Sheet + P&amp;L + Notes). Awaiting the firm&apos;s Draft Accounts template — upload it and this button unlocks.</p>
            <button data-testid="dl-draft" disabled className="btn-ghost text-xs flex items-center gap-2 justify-center opacity-50 cursor-not-allowed" title="Awaiting Draft Accounts template">
              <Download size={12} /> Coming soon
            </button>
          </div>

          <div className="border border-[var(--line)] rounded p-3 flex flex-col">
            <div className="flex items-center gap-2 mb-2"><FileCode2 size={16} className="text-[var(--navy)]" /><span className="font-medium text-sm">iXBRL (FRS 105)</span></div>
            <p className="text-xs text-[var(--ink-2)] flex-1 mb-3">Minimal FRS 105 micro-entity pack for TaxCalc.</p>
            <button
              data-testid="dl-ixbrl"
              disabled={!isApproved || !!busy}
              onClick={() => download(`/frtx/jobs/${job.id}/export/ixbrl`, `FRTX_iXBRL_${slug}_${job.year_end_date}.html`)}
              className="btn-primary text-xs flex items-center gap-2 justify-center disabled:opacity-40"
            >
              <Download size={12} /> Download .html
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Query group card: shows N similar transactions with AI suggestion + bulk resolve */
function QueryGroup({ group, coa, locked, resolve, resolveBulk }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const suggested = group.suggested;
  const bulkable = group.txs.length > 1;

  async function acceptAI() {
    if (!suggested) return;
    setBusy(true);
    await resolveBulk(group.txs, suggested);
    setBusy(false);
  }
  async function bulkResolveAs(coaName) {
    if (!coaName) return;
    setBusy(true);
    await resolveBulk(group.txs, coaName);
    setBusy(false);
  }

  const total = group.txs.reduce((s, t) => s + (t.is_debit ? t.amount : -t.amount), 0);
  const inCount = group.txs.filter(t => !t.is_debit).length;
  const outCount = group.txs.length - inCount;
  const testKey = group.sig.replace(/\s+/g, "_") || "unknown";

  return (
    <div className="border-l-4 border-l-[var(--err)] bg-[var(--err-bg,#FEE2E2)] p-3 rounded" data-testid={`q-group-${testKey}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{group.sample}</span>
            {bulkable && <span className="status-pill text-[10px] border-[var(--err)] text-[var(--err)] bg-white">×{group.txs.length}</span>}
            {outCount > 0 && <span className="status-pill text-[10px] border-[var(--ink)] text-[var(--ink)] bg-white" title={`${outCount} money-out transaction${outCount === 1 ? "" : "s"}`}>OUT · {outCount}</span>}
            {inCount > 0 && <span className="status-pill text-[10px] border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" title={`${inCount} money-in transaction${inCount === 1 ? "" : "s"}`}>IN · {inCount}</span>}
          </div>
          <div className="text-[11px] text-[var(--ink-2)] mt-0.5">
            {bulkable ? `${group.txs.length} similar transactions · net ${fmtSigned(-total)}` : `${group.txs[0].date} · ${group.txs[0].is_debit ? "money out" : "money in"} · ${fmt(group.txs[0].amount)}`}
          </div>
          {suggested && (
            <div className="text-[11px] text-[var(--navy)] mt-1 flex items-center gap-1">
              <Sparkles size={11} /> AI suggests: <b>{suggested}</b>
            </div>
          )}
          {group.txs[0].query_reason && <div className="text-[10px] text-[var(--ink-2)] italic mt-1">AI: {group.txs[0].query_reason}</div>}
        </div>

        <div className="flex flex-col gap-1.5 items-end">
          <div className="flex gap-1.5 flex-wrap justify-end">
            {suggested && (
              <button
                data-testid={`q-accept-${testKey}`}
                disabled={locked || busy}
                onClick={acceptAI}
                className="btn-primary !py-1 !px-2 text-[10px] flex items-center gap-1 disabled:opacity-40"
              >
                <CheckCircle2 size={11} /> Accept AI{bulkable ? ` for all ${group.txs.length}` : ""}
              </button>
            )}
            <select
              data-testid={`q-bulk-resolve-${testKey}`}
              disabled={locked || busy}
              defaultValue=""
              onChange={(e) => bulkResolveAs(e.target.value)}
              className="input-slim text-[10px] !py-1 min-w-[180px]"
            >
              <option value="">{bulkable ? `Resolve all as…` : `Resolve with COA…`}</option>
              {coa.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
            </select>
          </div>
          {bulkable && (
            <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-[var(--ink-2)] hover:text-[var(--ink)]">
              {expanded ? "Collapse" : `View all ${group.txs.length}`}
            </button>
          )}
        </div>
      </div>

      {expanded && bulkable && (
        <div className="mt-3 pt-3 border-t border-[var(--line)] space-y-1.5" data-testid={`q-group-expanded-${testKey}`}>
          {group.txs.map(t => (
            <div key={t.id} className="flex items-center gap-2 text-[11px]">
              <span className="font-mono-data text-[var(--ink-2)]">{t.date}</span>
              <span className={`status-pill text-[9px] ${t.is_debit ? "border-[var(--ink)] text-[var(--ink)]" : "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]"}`}>{t.is_debit ? "OUT" : "IN"}</span>
              <span className="flex-1 truncate">{t.description}</span>
              <span className={`font-mono-data ${t.is_debit ? "" : "text-[var(--ok)]"}`}>
                {t.is_debit ? "-" : "+"}{fmt(t.amount)}
              </span>
              <select
                disabled={locked || busy}
                defaultValue={t.coa_name || ""}
                onChange={(e) => resolve(t, e.target.value)}
                className="input-slim text-[10px] !py-0.5 min-w-[160px]"
                data-testid={`q-single-${t.id}`}
              >
                <option value="">Resolve…</option>
                {coa.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

